import { PublicNoticeQueries, PN_QUERY_KEYS } from '@api/services/public-notices/query';
import { LookupQueries, LOOKUP_QUERY_KEYS } from '@api/services/lookup';
import { usePublicNotices, useTableStore } from '@stores';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getStateNameFromCode } from '@utils/helpers';

import { buildPublicNoticesSearch } from './publicNoticeHelpers';
import { publicNoticesTableId } from '@pages/PublicNotices/public-notices-table/PublicNoticesTable';
import Filters from '@src/app-pages/common/components/filters/Filters';
import { createFilterSelectionActions } from '@src/app-pages/common/components/filters/filters.actions';
import { mapRecordCountsToItems } from '@src/app-pages/common/components/filters/filters.mappers';

const PublicNoticesFilter = () => {
  const navigate = useNavigate();
  const { getPageNumber, setPageNumber } = useTableStore();

  const { debouncedSearchText, districtCodes, searchText, setDistrictCodes, setSearchText, setStateCodes, stateCodes } =
    usePublicNotices();

  const pageNumber = getPageNumber(publicNoticesTableId);

  const updatePublicNoticesUrl = ({
    nextDistrictCodes = districtCodes,
    nextStateCodes = stateCodes,
    nextPageNumber = pageNumber,
    nextSearchText = searchText,
  }) => {
    navigate({
      to: '/public-notices',
      search: () =>
        buildPublicNoticesSearch({
          districtCodes: nextDistrictCodes,
          stateCodes: nextStateCodes,
          pageNumber: nextPageNumber,
          searchText: nextSearchText,
        }),
      replace: true,
    });
  };

  const handleSearchTextChange = (e) => {
    const value = e.target.value;
    setPageNumber(publicNoticesTableId, 1);
    setSearchText(value);
    updatePublicNoticesUrl({
      nextPageNumber: 1,
      nextSearchText: value,
    });
  };

  const { data: districtsData, isLoading: isLoadingDistricts } = useQuery({
    queryKey: [LOOKUP_QUERY_KEYS.GET_DISTRICTS],
    queryFn: LookupQueries.getDistricts,
  });

  const { data: filterData, isLoading } = useQuery({
    queryKey: [PN_QUERY_KEYS.GET_FILTER_COUNTS, stateCodes, districtCodes, debouncedSearchText],
    enabled: !isLoadingDistricts,
    queryFn: () =>
      PublicNoticeQueries.getFilterCounts({
        districtCodes,
        searchText: debouncedSearchText,
        states: stateCodes,
      }),
  });

  const selections = {
    state: stateCodes,
    district: districtCodes,
  };

  const setSelections = {
    state: setStateCodes,
    district: setDistrictCodes,
  };

  const { toggleSelection, clearSelection } = createFilterSelectionActions({
    selections,
    setSelections,
    onBeforeMutate: () => setPageNumber(publicNoticesTableId, 1),
    onAfterMutate: (nextSelections) => {
      updatePublicNoticesUrl({
        nextStateCodes: nextSelections.state,
        nextDistrictCodes: nextSelections.district,
        nextPageNumber: 1,
      });
    },
  });

  const handleToggle = (e, type) => {
    toggleSelection(type, e.target.value);
  };

  const stateItems = mapRecordCountsToItems(filterData?.stateCounts, (code) => getStateNameFromCode(code));
  const districtItems = mapRecordCountsToItems(
    filterData?.districtCodeCounts,
    (code) => districtsData?.[code]?.orgName
  );

  return (
    <Filters
      panelTitle='Filter Notices'
      loading={isLoading}
      search={{
        value: searchText,
        placeholder: 'search text',
        onChange: handleSearchTextChange,
      }}
      sections={[
        {
          id: 'states',
          title: 'States',
          type: 'state',
          selectedValues: stateCodes,
          items: stateItems,
          onToggle: handleToggle,
          onClear: () => clearSelection('state'),
          showClear: stateCodes.length > 0,
        },
        {
          id: 'districts',
          title: 'Districts',
          type: 'district',
          selectedValues: districtCodes,
          items: districtItems,
          onToggle: handleToggle,
          onClear: () => clearSelection('district'),
          showClear: districtCodes.length > 0,
        },
      ]}
    />
  );
};

export default PublicNoticesFilter;
